import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from './ProductCard';

const ProductFeaturesSection = ({ addToCart, toggleFavorite, isFavorite, cartItems }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const scrollContainerRefs = useRef({});
  const animationRefs = useRef({});
  const observerRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchFeatures();
  }, []);

  useEffect(() => {
    if (isMobile) {
      const allItemIds = items.map(item =>
        item.type === 'banner'
          ? `banner-${item.afterFeatureIndex}-${items.indexOf(item)}`
          : item.featureId
      );
      setVisibleItems(new Set(allItemIds));
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const itemId = entry.target.getAttribute('data-item-id');
            if (itemId) {
              setTimeout(() => {
                setVisibleItems((prev) => new Set([...prev, itemId]));
              }, 100);
            }
          }
        });
      },
      { rootMargin: '50px', threshold: 0.1 }
    );

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [items, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      const elements = document.querySelectorAll('[data-item-id]');
      elements.forEach((el) => {
        if (observerRef.current) observerRef.current.observe(el);
      });
    }
  }, [items, isMobile]);

  useEffect(() => {
    if (isMobile) return;

    const startAutoScroll = () => {
      const features = items.filter(item => item.type === 'feature');
      features.forEach((feature) => {
        const container = scrollContainerRefs.current[feature.featureId];
        if (container && window.innerWidth >= 768) {
          let isScrolling = false;
          let scrollPosition = 0;

          const scrollStep = () => {
            if (!isScrolling && container) {
              isScrolling = true;
              const distance = 300;
              const startScroll = container.scrollLeft;
              let startTime = null;

              const animation = (currentTime) => {
                if (!startTime) startTime = currentTime;
                const timeElapsed = currentTime - startTime;
                const duration = 800;
                const progress = Math.min(timeElapsed / duration, 1);
                const easeProgress = progress * (2 - progress);
                container.scrollLeft = startScroll + (distance * easeProgress);

                if (progress < 1) {
                  animationRefs.current[feature.featureId] = requestAnimationFrame(animation);
                } else {
                  scrollPosition += distance;
                  if (scrollPosition >= container.scrollWidth - container.clientWidth) {
                    scrollPosition = 0;
                    container.scrollLeft = 0;
                  }
                  setTimeout(() => { isScrolling = false; }, 2000);
                }
              };

              animationRefs.current[feature.featureId] = requestAnimationFrame(animation);
            }
          };

          const intervalId = setInterval(scrollStep, 2800);
          animationRefs.current[`${feature.featureId}_interval`] = intervalId;
        }
      });
    };

    if (items.length > 0) startAutoScroll();

    return () => {
      Object.entries(animationRefs.current).forEach(([key, value]) => {
        if (key.includes('_interval')) clearInterval(value);
        else if (value) cancelAnimationFrame(value);
      });
    };
  }, [items, isMobile]);

  const fetchFeatures = async () => {
    try {
      const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/api/home-features');
      const data = await response.json();
      if (data.success) setItems(data.data);
    } catch (error) {
      console.error('Error fetching features:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full py-8">
        <div className="text-center">
          <div
            className="inline-block animate-spin rounded-full h-12 w-12"
            style={{ borderBottom: '3px solid #4F7E17', borderTop: '3px solid #F5D19D', borderLeft: '3px solid transparent', borderRight: '3px solid transparent' }}
          />
        </div>
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <>
      {/* Półtawski Nowy font import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poltawski+Nowy:ital,wght@0,400;0,600;0,700;1,400;1,600&display=swap');

        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .see-all-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 28px 10px 20px;
          background: #fff;
          border: 2px solid #4F7E17;
          color: #4F7E17;
          font-family: 'Poltawski Nowy', serif;
          font-weight: 600;
          font-size: 15px;
          letter-spacing: 0.02em;
          cursor: pointer;
          overflow: hidden;
          transition: color 0.25s, background 0.25s, border-color 0.25s, box-shadow 0.25s;
          clip-path: polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%);
        }
        .see-all-btn:hover {
          background: #4F7E17;
          color: #fff;
          box-shadow: 0 6px 20px rgba(79,126,23,0.30);
        }
        .see-all-btn .arrow-icon {
          transition: transform 0.25s;
        }
        .see-all-btn:hover .arrow-icon {
          transform: translateX(4px);
        }
      `}</style>

      <div className="w-full mb-8" style={{ fontFamily: "'Poltawski Nowy', serif" }}>
        <div className="max-w-[1600px] mx-auto px-4 md:px-6">
          {items.map((item, index) => {

            if (item.type === 'banner') {
              const bannerId = `banner-${item.afterFeatureIndex}-${index}`;
              const isVisible = visibleItems.has(bannerId);

              return (
                <div key={bannerId} className="my-16 md:my-20" data-item-id={bannerId}>
                  <div
                    className={`w-full overflow-hidden transition-all duration-700 ${
                      isMobile ? 'opacity-100 translate-y-0'
                        : isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                    style={{ borderRadius: '16px', boxShadow: '0 6px 32px rgba(79,126,23,0.12)' }}
                  >
                    <img
                      src={item.imageUrl}
                      alt={`Banner after feature ${item.afterFeatureIndex}`}
                      className="w-full h-auto object-cover"
                      style={{ maxHeight: '400px', minHeight: '120px' }}
                      loading="lazy"
                    />
                  </div>
                </div>
              );
            }

            // Feature item
            const feature = item;
            const featureId = feature.featureId;
            const isVisible = visibleItems.has(featureId);

            return (
              <div
                key={featureId}
                className={`mb-12 first:mt-8 md:first:mt-12 ${
                  isMobile ? 'opacity-100 translate-y-0'
                    : `transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
                }`}
                data-item-id={featureId}
              >
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-3">
                  <div className="flex-1">
                    {/* Title */}
                    <div
                      style={{
                        fontFamily: "'Poltawski Nowy', serif",
                        fontSize: 'clamp(26px, 4vw, 42px)',
                        fontWeight: 700,
                        color: '#1a1a1a',
                        marginBottom: '8px',
                        lineHeight: 1.15,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {feature.title}
                    </div>

                    {/* Underline bar — Fresh Food gradient */}
                    <div style={{
                      width: '72px', height: '3px', borderRadius: '99px', marginBottom: '8px',
                      background: 'linear-gradient(90deg, #4F7E17, #F88702, #F0C400)',
                    }} />

                    {feature.description && (
                      <p
                        className="line-clamp-2"
                        style={{ fontSize: '14px', color: '#888', fontFamily: "'Poltawski Nowy', serif", fontStyle: 'italic', fontWeight: 400 }}
                      >
                        {feature.description}
                      </p>
                    )}
                  </div>

                  {/* See All button — desktop only, no arrow buttons */}
                  <div className="hidden md:flex items-center">
                    <button
                      className="see-all-btn"
                      onClick={() => navigate('/product')}
                    >
                      <span>See All</span>
                      <span className="arrow-icon" style={{ fontSize: '16px', lineHeight: 1 }}>→</span>
                    </button>
                  </div>
                </div>

                {/* Desktop: horizontal scroll */}
                <div
                  ref={(el) => (scrollContainerRefs.current[feature.featureId] = el)}
                  className="hidden md:flex gap-2 overflow-x-auto scrollbar-hide pb-4"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {feature.products.map((product) => (
                    <div key={product.productId} className="flex-shrink-0 w-[270px]">
                      <ProductCard
                        product={product}
                        addToCart={addToCart}
                        toggleFavorite={toggleFavorite}
                        isFavorite={isFavorite}
                        cartItems={cartItems}
                        navigate={navigate}
                      />
                    </div>
                  ))}
                </div>

                {/* Mobile: 2-col grid */}
                <div className="md:hidden flex justify-center">
                  <div className="grid grid-cols-2 gap-4 max-w-fit">
                    {feature.products.map((product) => (
                      <div key={product.productId}>
                        <ProductCard
                          product={product}
                          addToCart={addToCart}
                          toggleFavorite={toggleFavorite}
                          isFavorite={isFavorite}
                          cartItems={cartItems}
                          navigate={navigate}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ProductFeaturesSection;